/**
 * appCatalog.js — which launcher tiles a signed-in person sees.
 * Shaped by: signup prompt 42; decisions D-42-05, D-42-06 (MKT-18, MKT-30);
 *   A1 owner 2026-10-12 (institution and regional lists, QueryManager);
 *   G2a owner 2026-10-12 (FDE flag catalogs);
 *   G2b owner 2026-10-12 (CareMap Ads, BTYB, SpecialtyKB). PageGuide tile
 *   was not added: /dashboard/page-guide is not mounted for that flag alone.
 *   G2d owner 2026-10-12 (MorningReport learner HUD tiles, DSCR QueryManager,
 *   CareMap Ads for Institution Admin and org-role Marketing Executive).
 *   G2f owner 2026-10-12 (org Marketing Exec CareMap Ads only on ads-desk routers).
 *   G5b owner 2026-10-12 (dashboard tiles only where the active router mounts them).
 *   G2g owner 2026-10-12 (MorningReport Faculty tiles).
 * Invariants: grants are additive; a failed grant read does not add clinician
 *   tiles to a patient. Admin-level grant slugs add nothing. Parent / Guardian
 *   adds nothing until MKT-30 terms storage exists.
 *   Flag catalogs are additive and do not change ROLE_APP_IDS.
 *   The Apps menu lists openable apps only (owner 2026-10-12).
 * Related: firebase/firestore.rules userRoleGrants.
 */
import { getDashboardRouterKey } from "pages/dashboard/getDashboardRouterKey";
import { PRODUCT_TAGLINE } from "features/decisionPoint/data/fusionMsiBranding";
import { CLINICAL_HUD4_TEACHING } from "features/clinicalHud4/clinicalHud4WorkspaceConfig";
import { CLINICAL_HUD5_TEACHING } from "features/clinicalHud5/clinicalHud5WorkspaceConfig";
import FolderSharedRounded from "@mui/icons-material/FolderSharedRounded";
import { MASTERCHART_DASHBOARD_PATH } from "constants/masterChartRoutes";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import TranslateRounded from "@mui/icons-material/TranslateRounded";
import MapRounded from "@mui/icons-material/MapRounded";
import EditNoteRounded from "@mui/icons-material/EditNoteRounded";
import InsightsRounded from "@mui/icons-material/InsightsRounded";
import MonitorHeartRounded from "@mui/icons-material/MonitorHeartRounded";
import LeaderboardRounded from "@mui/icons-material/LeaderboardRounded";
import LoopRounded from "@mui/icons-material/LoopRounded";
import MenuBookRounded from "@mui/icons-material/MenuBookRounded";
import SportsEsportsRounded from "@mui/icons-material/SportsEsportsRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import ClassRounded from "@mui/icons-material/ClassRounded";
import EventAvailableRounded from "@mui/icons-material/EventAvailableRounded";
import ScienceRounded from "@mui/icons-material/ScienceRounded";
import NewspaperRounded from "@mui/icons-material/NewspaperRounded";
import FitnessCenterRounded from "@mui/icons-material/FitnessCenterRounded";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import PsychologyRounded from "@mui/icons-material/PsychologyRounded";
import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import HowToRegRounded from "@mui/icons-material/HowToRegRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import ViewInArRounded from "@mui/icons-material/ViewInArRounded";
import LocalHospitalRounded from "@mui/icons-material/LocalHospitalRounded";
import CampaignRounded from "@mui/icons-material/CampaignRounded";
import StorefrontRounded from "@mui/icons-material/StorefrontRounded";
import AutoStoriesRounded from "@mui/icons-material/AutoStoriesRounded";
import { isFusionMsiHost } from "features/decisionPoint/marketing/fusionMsiMarketingRouting";
import {
  isClinicalHudMarketingHost,
  isClinicalHudMarketingPath,
  isClinicalHudProductPath,
} from "features/clinicalHud/marketing/clinicalHudMarketingRouting";
import { getLiveMsi3Apps } from "features/msi3Teaching/msi3AppRegistry";
import { isMsi3Host } from "features/msi3Teaching/msi3HostRouting";
import { getAsiTeachingLauncherApps } from "features/msi3Teaching/asiTeachingAppRegistry";
import {
  isAsiMarketingHost,
  isAsiMarketingPath,
} from "features/asi/marketing/asiMarketingRouting";
import {
  FACT_PATTERN_PROFILE,
  RADIO_CALL_PROFILE,
} from "features/msi3Teaching/teachingAppProfiles";

/**
 * True when the title-bar launcher should show only ASI teaching tiles
 * (FactPattern, RadioCall, coming soon) — not the FusionMSI role catalog.
 *
 * Applies on the dedicated ASI host, on /asi-marketing preview paths on any
 * host, and while using ASI teaching product routes.
 */
export function shouldUseAsiTeachingLauncher(pathname = "") {
  if (isAsiMarketingHost()) return true;
  if (isAsiMarketingPath(pathname)) return true;
  const path = pathname || "";
  if (path === "/asi/teaching" || path.startsWith("/asi/teaching/")) {
    return true;
  }
  const teachingPrefixes = [
    FACT_PATTERN_PROFILE.route,
    RADIO_CALL_PROFILE.route,
  ];
  return teachingPrefixes.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Registry of every product the app launcher can surface.
 *
 * `to` is an in-app react-router route for shipped products; set `comingSoon`
 * (and omit `to`) for products without a destination yet. Icons come from
 * `@mui/icons-material` and can be swapped freely.
 *
 * Prompt 21 (owner 2026-10-08): `comingSoon` is a launcher watermark only.
 * It is not access control. Routes and Firestore rules stay as they are.
 * `hideFromLauncher` keeps a registry id resolvable without painting a tile.
 *
 * Optional `visibleOnRestrictedHosts`: when true, the tile may appear on
 * fusionmsi.com / DecisionPoint surfaces where the launcher is otherwise
 * filtered to opted-in apps only.
 */
export const APP_REGISTRY = {
  masterchart: {
    id: "masterchart",
    name: "MasterChart",
    description: "Unified personal health record",
    icon: FolderSharedRounded,
    to: MASTERCHART_DASHBOARD_PATH,
    comingSoon: false,
  },
  carestart: {
    id: "carestart",
    name: "CareStart",
    description: "Guided symptom check & triage",
    icon: HealthAndSafetyRounded,
    to: "/carestart3",
    comingSoon: false,
  },
  "medical-library": {
    id: "medical-library",
    name: "Medical Library",
    description: "Ask medical questions",
    icon: MenuBookRounded,
    to: "/dashboard/medical-library",
    comingSoon: true,
  },
  "care-gaps": {
    id: "care-gaps",
    name: "Care Gaps",
    description: "Follow-up and screening reminders",
    icon: EventAvailableRounded,
    to: "/dashboard/care-gaps",
    comingSoon: true,
  },
  results: {
    id: "results",
    name: "Results",
    description: "Lab and imaging notifications",
    icon: ScienceRounded,
    to: "/dashboard/results",
    comingSoon: true,
  },
  voxpont: {
    id: "voxpont",
    name: "VoxPont",
    description: "Live medical translation",
    icon: TranslateRounded,
    // Medical flagship surface; page embeds VoxPont beta gate (OQ-1).
    to: "/voxpont12",
    comingSoon: false,
  },
  caremap: {
    id: "caremap",
    name: "CareMap",
    description: "Find nearby care",
    icon: MapRounded,
    // Prompt 27 / D-20-02: the tile opens the public /caremap2 map.
    // Same locations read as /caremap. Not the CareStart-embedded variant.
    to: "/caremap2",
    comingSoon: false,
  },
  healthcontours: {
    id: "healthcontours",
    name: "HealthContours",
    description: "Facility intelligence and community contours",
    icon: MapRounded,
    to: "/healthcontours",
    comingSoon: true,
  },
  chartmind: {
    id: "chartmind",
    name: "ChartMind - OP",
    // Prompt 21: one ChartMind tile opens ClinicalHUD2. The id stays resolvable.
    description: "Encounter workspace with Even Hub dictation (not ClinicalHUD v1)",
    icon: EditNoteRounded,
    to: "/clinical-hud-2",
    comingSoon: false,
    visibleOnRestrictedHosts: true,
  },
  decisionpoint: {
    id: "decisionpoint",
    name: "DecisionPoint",
    description: PRODUCT_TAGLINE,
    icon: InsightsRounded,
    // Clinical co-pilot; route sits behind DpRouteGate (OQ-2).
    to: "/dp-display",
    comingSoon: false,
  },
  savoircare: {
    id: "savoircare",
    name: "SavoirCare",
    description: "ED patient-flow board",
    icon: MonitorHeartRounded,
    to: "/dashboard/savoircare",
    comingSoon: false,
  },
  fusionlead: {
    id: "fusionlead",
    name: "FusionLead",
    description: "Institution leadership cockpit",
    icon: LeaderboardRounded,
    to: "/dashboard/reports",
    comingSoon: false,
  },
  loopmanager: {
    id: "loopmanager",
    name: "LoopManager",
    description: "Loop health & network analytics",
    icon: LoopRounded,
    to: "/dashboard/readiness",
    comingSoon: false,
  },
  news: {
    id: "news",
    name: "News",
    description: "Health headlines & topics",
    icon: NewspaperRounded,
    to: "/news",
    comingSoon: true,
    visibleOnRestrictedHosts: true,
  },
  fitness: {
    id: "fitness",
    name: "Fitness",
    description: "Garmin activity & wellness",
    icon: FitnessCenterRounded,
    to: "/fitness",
    comingSoon: true,
    visibleOnRestrictedHosts: true,
  },
  email: {
    id: "email",
    name: "Email",
    description: "Secure clinical messaging",
    icon: MailOutlineRounded,
    to: "/email",
    comingSoon: true,
    visibleOnRestrictedHosts: true,
  },
  "carestart-plus": {
    id: "carestart-plus",
    name: "CareStart-Plus",
    description: "Modular patient TCIs (registration, triage, HPI, discharge)",
    icon: HealthAndSafetyRounded,
    to: "/carestart-plus",
    comingSoon: true,
  },
  "visit-start": {
    id: "visit-start",
    name: "VisitStart",
    description: "Clinic visit intake session",
    icon: HowToRegRounded,
    to: "/visit-start",
    comingSoon: false,
  },
  "chartmind-plus": {
    id: "chartmind-plus",
    name: "ChartMind-Plus",
    description: "Clinician modular TCI shell (differential on day one)",
    icon: EditNoteRounded,
    to: "/chartmind-plus",
    comingSoon: true,
  },
  chartMindI: {
    id: "chartMindI",
    name: "ChartMind-I",
    description: "Inpatient stay documentation (not ChartMind-I2)",
    icon: LocalHospitalRounded,
    to: "/chartmind-i",
    comingSoon: true,
  },
  chartMindI2: {
    id: "chartMindI2",
    name: "ChartMind-I2",
    description: "Voice-first inpatient workspace with care tasks (not ChartMind-I)",
    icon: LocalHospitalRounded,
    to: "/chartmind-i2",
    comingSoon: false,
  },
  appliedsuperintelligence: {
    id: "appliedsuperintelligence",
    name: "Applied Superintelligence",
    description: "Interaction workflows & training canvas",
    icon: AccountTreeRounded,
    to: "/asi/templates",
    comingSoon: true,
  },
  clinicalHud: {
    id: "clinicalHud",
    name: "ClinicalHUD",
    description: "Ambient DDx and teaching pearls (not ClinicalHUD2)",
    icon: VisibilityRounded,
    to: "/clinical-hud",
    comingSoon: true,
    visibleOnRestrictedHosts: true,
  },
  clinicalHud2: {
    id: "clinicalHud2",
    name: "ClinicalHUD2",
    description: "Encounter workspace with Even Hub dictation (not ClinicalHUD v1)",
    icon: ViewInArRounded,
    to: "/clinical-hud-2",
    comingSoon: false,
    visibleOnRestrictedHosts: true,
    // Prompt 21: the ChartMind tile is the one card. This id stays for other code.
    hideFromLauncher: true,
  },
  chartMind7: {
    id: "chartMind7",
    name: "ChartMind - IP",
    description: "Specialty and setting encounter workspace",
    icon: ViewInArRounded,
    to: "/chartmind-7?setting=inpatient",
    comingSoon: false,
    visibleOnRestrictedHosts: true,
  },
  clinicalHud3: {
    id: "clinicalHud3",
    name: "ClinicalHUD 3",
    description: "Educational simulated cases for medical practitioners",
    icon: MenuBookRounded,
    to: "/clinical-hud-3",
    comingSoon: true,
    visibleOnRestrictedHosts: true,
  },
  clinicalHud4: {
    id: "clinicalHud4",
    name: CLINICAL_HUD4_TEACHING.productTitle,
    description: CLINICAL_HUD4_TEACHING.productTagline,
    icon: SportsEsportsRounded,
    to: "/clinical-hud-4",
    comingSoon: false,
    visibleOnRestrictedHosts: true,
  },
  clinicalHud5: {
    id: "clinicalHud5",
    name: CLINICAL_HUD5_TEACHING.productTitle,
    description: CLINICAL_HUD5_TEACHING.productTagline,
    icon: SchoolRounded,
    to: CLINICAL_HUD5_TEACHING.listPath,
    comingSoon: false,
    visibleOnRestrictedHosts: true,
  },
  // Opens the existing Prompts Manager. The route keeps its own guard
  // (FDE-T and others already land there). Not wrapped in RequireLauncherApp.
  querymanager: {
    id: "querymanager",
    name: "QueryManager",
    description: "Edit your region's prompt layer",
    icon: PsychologyRounded,
    to: "/dashboard/prompts",
    comingSoon: false,
  },
  // Existing screens. Routes keep their own guards. Not wrapped in RequireLauncherApp.
  "caremap-ads": {
    id: "caremap-ads",
    name: "CareMap Ads",
    description: "Manage your CareMap zip-slot ads",
    icon: CampaignRounded,
    to: "/dashboard/advertising/slots",
    comingSoon: false,
  },
  btyb: {
    id: "btyb",
    name: "BTYB",
    description: "Run your BTYB sponsor auction",
    icon: StorefrontRounded,
    to: "/dashboard/btyb",
    comingSoon: false,
  },
  specialtykb: {
    id: "specialtykb",
    name: "SpecialtyKB",
    description: "Review specialty knowledge content",
    icon: AutoStoriesRounded,
    to: "/specialty-kb/editors",
    comingSoon: false,
  },
  // Same people RequireMsi3Faculty lets through: an approved grant with an
  // orgId, or a platform admin. /msi3/faculty/cases is outside /dashboard.
  "morningreport-faculty": {
    id: "morningreport-faculty",
    name: "MorningReport Faculty",
    description: "Manage your school's MorningReport cases",
    icon: ClassRounded,
    to: "/msi3/faculty/cases",
    comingSoon: false,
    visibleOnRestrictedHosts: true,
  },
};

/**
 * Every registry id (registry insertion order). Platform admin (global_admin)
 * sees this full list. Institution and regional admins use explicit lists.
 */
export const ALL_APP_IDS = Object.keys(APP_REGISTRY);

/** @deprecated Prefer ALL_APP_IDS. Only global_admin still assigns this list. */
export const ADMIN_CORE_APP_IDS = ALL_APP_IDS;

/**
 * The knob to edit per role: role key -> ordered list of app ids shown in the
 * launcher. Order here is the display order.
 *
 * End-user role matrix (owner-binding for non-admin roles):
 * - news → platform admin via ALL_APP_IDS. Patient removed 2026-10-12. Clinician removed 2026-10-12. Institution and regional removed 2026-10-12.
 * - email → platform admin via ALL_APP_IDS. Patient and facility removed 2026-10-12. Clinician removed 2026-10-12. Institution and regional removed 2026-10-12.
 * - fitness → platform admin via ALL_APP_IDS. Patient removed 2026-10-12. Institution and regional removed 2026-10-12.
 * - clinicalHud → clinician; platform admin via ALL_APP_IDS. FusionMSI restricted-host launcher
 * - clinicalHud2 → clinician; platform admin via ALL_APP_IDS. FusionMSI restricted-host launcher
 * - chartMind7 → clinician; platform admin via ALL_APP_IDS. FusionMSI restricted-host launcher
 * - clinicalHud3 → MorningReport learner flag, and platform admin via ALL_APP_IDS.
 *   Clinician tile removed 2026-10-12. The page stays open (sign-up funnel).
 * - clinicalHud4 → clinician; platform admin via ALL_APP_IDS. FusionMSI restricted-host launcher
 * - clinicalHud5 → clinician; FusionMSI restricted-host launcher. Platform
 *   admin still receives it via ALL_APP_IDS.
 *
 * Admin catalogs (owner 2026-10-12):
 * - global_admin keeps ALL_APP_IDS, including querymanager.
 * - admin (institution) is visit-start, caremap, caremap-ads, and querymanager
 *   (P3 for their institution only; owner 2026-10-13).
 * - dscr-admin is querymanager only. Query Manager still limits that role to
 *   DSCR chain position 1.
 * - regional_admin is visit-start, caremap, and querymanager.
 * shouldBypassRestrictedLauncherHost is unchanged: all three admin tiers
 * still skip the restricted-host clip and see their own catalog there.
 */
export const ROLE_APP_IDS = {
  patient: [
    "masterchart",
    "carestart",
    "visit-start",
    "medical-library",
    "voxpont",
    "caremap",
  ],
  clinician: [
    "chartmind",
    "chartMind7",
    "decisionpoint",
    "clinicalHud",
    "clinicalHud2",
    "clinicalHud4",
    "clinicalHud5",
    "voxpont",
  ],
  // Clinical-governance tiles (FusionLead, LoopManager) removed from facility (OQ-3).
  facility: ["savoircare", "caremap", "visit-start"],
  admin: ["visit-start", "caremap", "caremap-ads", "querymanager"],
  regional_admin: ["visit-start", "caremap", "querymanager"],
  "dscr-admin": ["querymanager"],
  global_admin: ALL_APP_IDS,
  marketingExecutive: ["caremap-ads"],
  btybMarketer: ["btyb"],
};

/**
 * Tiles added when a live FDE flag is true. Not a users.role catalog.
 * isFdeC has no tiles (owner 2026-10-12) and is not a key here.
 */
export const FLAG_APP_IDS = {
  isFdeDc: ["loopmanager", "fusionlead"],
  hasGtmAccess: ["loopmanager", "fusionlead"],
  isFdeT: ["loopmanager"],
  specialtyKbReviewer: ["specialtykb"],
  // msi3LearnerProfiles/{uid} exists. A read error stays false. clinicalHud3LearnerProfile
  // is a different document and does not add these tiles.
  hasMorningReportProfile: ["clinicalHud3", "clinicalHud4", "clinicalHud5"],
  // Same check as RequireMsi3Faculty. A failed grant read stays false
  // unless the caller is a platform admin, which the gate also allows.
  hasMorningReportFaculty: ["morningreport-faculty", "clinicalHud5"],
  // activeOrgRole.role only. users.role marketingExecutive already has its catalog.
  // The dashboard filter keeps the tile where the ads desk mounts.
  isOrgMarketingExecutive: ["caremap-ads"],
};

/**
 * App ids whose /dashboard destination each router mounts and does not send
 * to a fallback redirect. RequireLauncherApp still follows the catalog.
 * ZipSlotAdsGate denies the facility role on the ads desk, so caremap-ads
 * stays off `facility` (G2f: Institution Admin and Clinician only).
 * globalAdmin lists every /dashboard app in APP_REGISTRY.
 */
export const ROUTER_DASHBOARD_APPS = {
  patient: ["masterchart", "medical-library", "care-gaps", "results"],
  professional: ["caremap-ads"],
  facility: ["savoircare", "fusionlead", "loopmanager"],
  institutionAdmin: [
    "masterchart",
    "savoircare",
    "fusionlead",
    "loopmanager",
    "querymanager",
    "caremap-ads",
  ],
  regionalAdmin: [
    "masterchart",
    "savoircare",
    "fusionlead",
    "loopmanager",
    "querymanager",
  ],
  globalAdmin: [
    "masterchart",
    "medical-library",
    "care-gaps",
    "results",
    "savoircare",
    "fusionlead",
    "loopmanager",
    "querymanager",
    "caremap-ads",
    "btyb",
  ],
  marketingExecutive: ["caremap-ads"],
  btybMarketer: ["btyb"],
  notSetUp: [],
  onboarding: [],
  regionalAdminMissingRegion: [],
};

/** Org / regional / platform admin keys. They bypass restricted-host clipping. Only global_admin's catalog is ALL_APP_IDS. */
export const ADMIN_LAUNCHER_ROLES = new Set([
  "admin",
  "regional_admin",
  "global_admin",
]);

/**
 * Resolve the launcher role key from auth state.
 *
 * Precedence: global admin > regional admin > org admin > account role.
 * The Firestore `professional` role maps to the `clinician` launcher key.
 * Returns `null` when there is no usable role.
 */
export function getLauncherRole({ userData, isGlobalAdmin, isRegionalAdmin } = {}) {
  if (isGlobalAdmin) return "global_admin";
  if (isRegionalAdmin) return "regional_admin";

  const role = userData?.role;
  // Fallback for admin tiers stored as a Firestore role rather than a JWT claim,
  // so a global/regional admin still gets the full catalog even if the custom
  // claim hasn't propagated to the current token.
  if (role === "global_admin") return "global_admin";
  if (role === "regional_admin") return "regional_admin";
  if (role === "admin") return "admin";
  // Prompt 21: dev code 300003 is stored as professional-chartmind. The owner
  // treats that code as the clinician, so the launcher uses the clinician catalog.
  if (role === "professional" || role === "professional-chartmind") return "clinician";
  if (role === "patient" || role === "p2" || role === "p4") return "patient";
  if (role === "facility") return "facility";
  if (role === "marketingExecutive") return "marketingExecutive";
  if (role === "btybMarketer") return "btybMarketer";
  if (role === "dscr-admin") return "dscr-admin";
  return null;
}

/**
 * Given auth state, return the ordered, hydrated list of app entries the user
 * should see in the launcher. Unknown ids are skipped. Returns `[]` when the
 * user has no launcher role, no flag catalog, and no ASI grant.
 *
 * `hasAsiAccess` is additive: invited ASI role/staff holders see the
 * Applied Superintelligence tile even when their FusionMSI role would not.
 * Platform admin still receives it via ALL_APP_IDS without this flag.
 *
 * `hasChartMindIAccess` is additive the same way: grant holders see ChartMind-I
 * even when it is not in their role catalog. Platform admin still receives it
 * via ALL_APP_IDS. Unlike ClinicalHUD, the tile is not shown to every clinician.
 * `hasChartMindI2Access` mirrors that pattern for ChartMind-I2.
 */
/**
 * Map a server grant slug onto a launcher catalog.
 * Parent / Guardian is the patient catalog only after terms (MKT-30). That
 * acceptance record is not stored yet, so those slugs add nothing.
 * Admin power comes only from users.role or the JWT admin claim (owner 2026-10-12).
 */
export function launcherRoleForGrantSlug(slug) {
  if (slug === "admin" || slug === "regional_admin" || slug === "global_admin") {
    return null;
  }
  if (slug === "parentGuardian" || slug === "parent-guardian") return null;
  if (slug === "clinician" || slug === "professional") return "clinician";
  if (slug === "patient") return "patient";
  if (slug === "facility") return "facility";
  if (typeof slug === "string" && ROLE_APP_IDS[slug]) return slug;
  return null;
}

/**
 * Launcher catalogs for this account: users.role plus server grants.
 * A failed grant read keeps the catalog users.role already gives (D-42-06).
 */
export function launcherRolesForAuth(authState = {}) {
  const primary = getLauncherRole(authState);
  if (authState.grantsReadFailed) {
    return primary ? [primary] : [];
  }
  const roles = [];
  if (primary) roles.push(primary);
  const grants = Array.isArray(authState.roleGrants) ? authState.roleGrants : [];
  grants.forEach((grant) => {
    const slug = typeof grant === "string" ? grant : grant?.roleSlug;
    const mapped = launcherRoleForGrantSlug(slug);
    if (mapped && !roles.includes(mapped)) roles.push(mapped);
  });
  return roles;
}

function flagAppIdsFor(authState = {}) {
  const seen = new Set();
  const ids = [];
  Object.entries(FLAG_APP_IDS).forEach(([flag, appIds]) => {
    if (!authState[flag]) return;
    (appIds || []).forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
  });
  return ids;
}

export function getAppsForUser(authState = {}) {
  const roleKeys = launcherRolesForAuth(authState);
  const roleKey = roleKeys[0] || null;
  const hasAsiAccess = Boolean(authState.hasAsiAccess);
  const hasChartMindIAccess = Boolean(authState.hasChartMindIAccess);
  const hasChartMindI2Access = Boolean(authState.hasChartMindI2Access);
  const asiApp = APP_REGISTRY.appliedsuperintelligence;
  const chartMindIApp = APP_REGISTRY.chartMindI;
  const chartMindI2App = APP_REGISTRY.chartMindI2;

  const extrasFor = (apps = []) => {
    const extra = [];
    if (hasAsiAccess && asiApp && !apps.some((app) => app.id === asiApp.id)) {
      extra.push(asiApp);
    }
    if (
      hasChartMindIAccess &&
      chartMindIApp &&
      !apps.some((app) => app.id === chartMindIApp.id)
    ) {
      extra.push(chartMindIApp);
    }
    if (
      hasChartMindI2Access &&
      chartMindI2App &&
      !apps.some((app) => app.id === chartMindI2App.id)
    ) {
      extra.push(chartMindI2App);
    }
    return extra;
  };

  const seen = new Set();
  const ids = [];
  roleKeys.forEach((key) => {
    (ROLE_APP_IDS[key] || []).forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
  });
  // Flag tiles are additive, including when users.role has no launcher catalog.
  flagAppIdsFor(authState).forEach((id) => {
    if (seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });

  if (!roleKey && ids.length === 0) {
    return extrasFor([]);
  }

  const apps = ids.map((id) => APP_REGISTRY[id]).filter((app) => Boolean(app));
  const extra = extrasFor(apps);
  const withExtras = extra.length ? [...apps, ...extra] : apps;
  return appsOpenOnActiveDashboard(
    withoutPatientOnlyVoxPont(withExtras, roleKeys, authState),
    authState,
  );
}

/**
 * Drop a /dashboard tile when the active router does not mount that page.
 * Top-level destinations stay. globalAdmin's set covers every dashboard app.
 */
function appsOpenOnActiveDashboard(apps, authState) {
  const routerKey = getDashboardRouterKey({
    userData: authState.userData,
    isGlobalAdmin: Boolean(authState.isGlobalAdmin),
    isFdeDc: Boolean(authState.isFdeDc),
    isFdeC: Boolean(authState.isFdeC),
    isFdeT: Boolean(authState.isFdeT),
    hasGtmAccess: Boolean(authState.hasGtmAccess),
  });
  const mounted = new Set(ROUTER_DASHBOARD_APPS[routerKey] || []);
  return apps.filter((app) => {
    const to = typeof app?.to === "string" ? app.to : "";
    if (!to.startsWith("/dashboard/")) return true;
    return mounted.has(app.id);
  });
}

/**
 * Patient VoxPont is a tile only while beta is active (owner 2026-10-12).
 * A clinician or admin catalog that also lists voxpont keeps the tile.
 */
function withoutPatientOnlyVoxPont(apps, roleKeys, authState) {
  if (authState.hasVoxPontBeta) return apps;
  const anotherCatalogHasVoxPont = roleKeys.some(
    (key) => key !== "patient" && (ROLE_APP_IDS[key] || []).includes("voxpont"),
  );
  if (anotherCatalogHasVoxPont || !roleKeys.includes("patient")) return apps;
  return apps.filter((app) => app.id !== "voxpont");
}

/**
 * True on fusionmsi.com / DecisionPoint path prefixes where the launcher should
 * only show apps that opted into `visibleOnRestrictedHosts`.
 */
export function isRestrictedLauncherHost(pathname = "") {
  const path = pathname || "";
  return (
    isFusionMsiHost() ||
    isClinicalHudMarketingHost() ||
    isClinicalHudMarketingPath(path) ||
    isClinicalHudProductPath(path) ||
    path === "/dp" ||
    path.startsWith("/dp-") ||
    path.startsWith("/dp/")
  );
}

/**
 * Admin-tier catalogs are not clipped on fusionmsi.com / DecisionPoint hosts.
 * Patients, clinicians, and facility users still use the opted-in subset.
 */
export function shouldBypassRestrictedLauncherHost(authState = {}) {
  if (isMsi3Host()) return false;
  return ADMIN_LAUNCHER_ROLES.has(getLauncherRole(authState));
}

/**
 * On restricted hosts, keep only apps with `visibleOnRestrictedHosts: true`.
 * Elsewhere (or when `bypassRestrictedHostFilter` is set), return the list unchanged.
 */
export function filterAppsForRestrictedHost(apps, pathname, options = {}) {
  if (options.bypassRestrictedHostFilter) return apps;
  if (!isRestrictedLauncherHost(pathname)) return apps;
  return (apps || []).filter((app) => app?.visibleOnRestrictedHosts === true);
}

/**
 * Prompt 21: Coming Soon tiles stay in their relative order, after every active tile.
 * `hideFromLauncher` drops a registry row from the painted list without deleting the id.
 */
export function orderLauncherApps(apps = []) {
  const visible = (apps || []).filter((app) => app && app.hideFromLauncher !== true);
  const active = [];
  const soon = [];
  for (const app of visible) {
    if (app.comingSoon === true) soon.push(app);
    else active.push(app);
  }
  return [...active, ...soon];
}

/**
 * Role catalog plus restricted-host filter. Admin tiers keep their own
 * catalog on fusionmsi.com. Only global_admin's catalog is ALL_APP_IDS.
 */
export function getVisibleLauncherApps(authState = {}, pathname = "") {
  if (isMsi3Host()) {
    const liveIds = new Set(getLiveMsi3Apps().map((app) => app.id));
    return orderLauncherApps(
      getAppsForUser(authState).filter((app) => liveIds.has(app.id)),
    );
  }
  if (shouldUseAsiTeachingLauncher(pathname)) {
    // ASI teaching launcher: FactPattern, RadioCall, CoherentSpace (admins), coming-soon.
    // Does not use the FusionMSI role catalog.
    return getAsiTeachingLauncherApps({
      isAdmin: Boolean(authState.isGlobalAdmin || authState.isRegionalAdmin),
    });
  }
  return orderLauncherApps(
    filterAppsForRestrictedHost(
      getAppsForUser(authState),
      pathname,
      {
        bypassRestrictedHostFilter: shouldBypassRestrictedLauncherHost(authState),
      },
    ),
  );
}
